import { validateWalletAddress } from '@btcdirect/functions/src/validators/wallet-address';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useClientInfoStore } from './client-info';
import { useConfigurationStore } from './configuration';
import { Currency, useCurrenciesStore } from './currencies';

export type WalletAddressVerificationStatus = 'open' | 'pending' | 'verified' | 'rejected' | 'disabled';
export type WalletAddressType = 'manual' | 'select';

export interface WalletAddress {
    address: string;
    currency: string;
    destinationTag?: number;
    identifier?: string;
    name?: string;
    status?: WalletAddressVerificationStatus;
}

const { warn, debug } = useLogger();

export const useWalletAddressesStore = defineStore('wallet-addresses', () => {
    const clientInfoStore = useClientInfoStore();
    const configurationStore = useConfigurationStore();
    const currenciesStore = useCurrenciesStore();

    // True if addresses are provided through the 'addresses' or 'set-parameters' method
    const addressesAreManuallyProvided = ref<boolean>(false);

    const currentWalletAddressType = ref<WalletAddressType>(null);
    const wallets = ref<WalletAddress[]>([]);
    const selected = ref<WalletAddress>(null);
    const manual = ref<WalletAddress>(null);

    const cryptos = computed<string[]>(() =>
        !!clientInfoStore.client.walletVerificationRequired
            ? currenciesStore.cryptoCurrencies
            : Array.from(new Set(wallets.value.map((address) => address.currency)))
    );

    const availableWalletAddressCurrencies = computed<string[]>(() =>
        !!clientInfoStore.client.walletVerificationRequired
            ? cryptos.value
            : cryptos.value.filter((crypto) => currenciesStore.cryptoCurrencies.includes(crypto))
    );

    watch(
        () => currenciesStore.selectedCryptoCurrency,
        (received: Currency) => {
            if (!received || received.code === selected.value?.currency || currentWalletAddressType.value === 'manual') {
                return;
            }

            if (!cryptos.value.includes(received.code)) {
                selectWalletAddress(null);
                return;
            }

            selectWalletAddress(wallets.value.find((address) => address.currency === received.code));
        }
    );

    // If there are wallet addresses set (via addresses method) and wallet verification is required, clear the addresses
    watch(
        () => clientInfoStore.client.walletVerificationRequired,
        (required: boolean) => {
            if (!required || configurationStore.isUnifiedCheckout) {
                return;
            }

            setWalletAddresses(null);
        }
    );

    function addWalletAddress(walletAddress: WalletAddress, select: boolean = true): void {
        if (!walletAddress.identifier) {
            walletAddress.identifier = getIdentifier(walletAddress);
        }

        const existing = wallets.value.find((address) => address.identifier === walletAddress.identifier);
        if (!existing) {
            wallets.value.push(walletAddress);

            if (select) {
                selectWalletAddress(walletAddress);
            }

            return;
        }

        if ((walletAddress.name && existing.name !== walletAddress.name) || (walletAddress.status && existing.status !== walletAddress.status)) {
            debug('Updating existing wallet address', `Address: ${walletAddress.address}`, `Currency: ${walletAddress.currency}`);

            // Only update the name and status as those can come from user info while the rest should not change
            existing.name = walletAddress.name;
            existing.status = walletAddress.status;
        }

        if (select) {
            selectWalletAddress(walletAddress);
        }
    }

    function addWalletAddresses(walletAddresses: WalletAddress[]): void {
        if (!walletAddresses?.length) {
            return;
        }

        for (const wallet of walletAddresses) {
            addWalletAddress(wallet, false);
        }
    }

    function setWalletAddresses(walletAddresses: WalletAddress[]): void {
        if (!walletAddresses || !walletAddresses.length) {
            wallets.value = [];
            selectWalletAddress(null);
            return;
        }

        wallets.value = walletAddresses.map((walletAddress) => ({
            ...walletAddress,
            identifier: getIdentifier(walletAddress)
        }));

        if (!currenciesStore.selectedCryptoCurrency || !cryptos.value.includes(currenciesStore.selectedCryptoCurrency.code)) {
            selectWalletAddress(!clientInfoStore.client.walletVerificationRequired ? wallets.value[0] : null);
            return;
        }

        if (!manual.value && !selected.value) {
            selectWalletAddress(wallets.value.find((address) => address.currency === currenciesStore.selectedCryptoCurrency.code));
            return;
        }

        if (currentWalletAddressType.value === 'manual' && !!manual.value) {
            if (wallets.value.some((address) => address.address === manual.value.address && address.currency === manual.value.currency)) {
                selectWalletAddress(manual.value);
                return;
            }

            setManualAddress(manual.value);
            return;
        }

        selectWalletAddress(selected.value);
    }

    function selectWalletAddress(walletAddress: WalletAddress): void {
        if (!walletAddress) {
            selected.value = null;
            return;
        }

        currentWalletAddressType.value = 'select';

        if (!!selected.value && walletAddress.identifier === selected.value.identifier) {
            currenciesStore.setCryptoCurrency(selected.value.currency);
            return;
        }

        if (!wallets.value.find((address) => address.identifier === walletAddress.identifier)) {
            warn(`Selected wallet address "${walletAddress.address}" (${walletAddress.currency}) is not in the provided wallet addresses`);
        }

        selected.value = walletAddress;
        currenciesStore.setCryptoCurrency(selected.value.currency);
    }

    function setManualAddress(walletAddress: WalletAddress): void {
        currentWalletAddressType.value = 'manual';

        if (!walletAddress) {
            manual.value = null;
            return;
        }

        if (!validateWalletAddress(walletAddress.address, walletAddress.currency)) {
            return;
        }

        walletAddress.identifier = getIdentifier(walletAddress);

        addWalletAddress(walletAddress, false);
        manual.value = walletAddress;
    }

    function setCurrentWalletAddressType(type: WalletAddressType): void {
        currentWalletAddressType.value = type;
    }

    function setAddressesAreManuallyProvided(value: boolean): void {
        addressesAreManuallyProvided.value = value;
    }

    return {
        walletAddresses: computed<WalletAddress[]>(() => wallets.value),
        currentWalletAddress: computed<WalletAddress>(() => (currentWalletAddressType.value === 'manual' ? manual.value : selected.value)),
        selectedWalletAddress: computed<WalletAddress>(() => selected.value),
        currentWalletAddressType: computed<WalletAddressType>(() => currentWalletAddressType.value),
        addressesAreManuallyProvided: computed<boolean>(() => addressesAreManuallyProvided.value),
        availableWalletAddressCurrencies,
        addWalletAddress,
        addWalletAddresses,
        setWalletAddresses,
        selectWalletAddress,
        setManualAddress,
        setCurrentWalletAddressType,
        setAddressesAreManuallyProvided
    };
});

/**
 * Return the wallet addresses' identifier or create one if it doesn't exist
 * @param walletAddress
 */
function getIdentifier(walletAddress: WalletAddress): string {
    if ('identifier' in walletAddress && !!walletAddress.identifier) {
        return walletAddress.identifier;
    }

    return `${walletAddress.address}-${walletAddress.currency}`;
}
